import { createContext, useContext } from 'react'
import type { AxiosInstance } from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

/**
 * The partner space (/api/partenaires/, programme_partenaires/espace.py).
 * One space, two doors: the "Partner" settings section of the dashboard for
 * clients (this lot), the separate partner address for public partners later.
 * Amounts are CAD strings ("28.34"); labels are translated here, not on the server.
 */

export type PartnerClientState = 'inscrit' | 'essai' | 'payant' | 'annule'
export type InstalmentState = 'attente' | 'a_verser' | 'verse' | 'annule'

export interface PartnerProgram {
  /** False until the partner agreement is published: no one can join alone yet. */
  ouvert: boolean
  entente_version: string
  entente_url: string
  /** The agreement is bilingual (section 15.6): its English page, for a partner reading in English. */
  entente_url_en: string
  bareme: { forfait: string; montant: string }[]
  credit_plafond: string
}

export interface PartnerClient {
  entreprise: string
  le: string
  source: 'lien' | 'code' | 'rapprochement'
  etat: PartnerClientState
  prime: {
    montant: string
    forfait: string
    annuel: boolean
    etat: 'en_cours' | 'soldee' | 'annulee'
    mensualites: { rang: number; montant: string; etat: InstalmentState }[]
  } | null
  /** 10 % of the AI receptionist paid in the client's first 12 months: estimated, then due at month 13. */
  bonus_ia: { montant: string; etat: 'estime' | 'a_verser' | 'verse' | 'rien' | 'annule'; du_le: string } | null
  /** What this client has earned the partner so far: instalments due or paid, plus commissions. */
  gagne: string
}

export interface PartnerCommissions {
  a_recevoir: string
  recues: string
  seuil: string
  /** Something is owed but under the threshold: it waits for next month. */
  sous_seuil: boolean
  /** 'retiree': the invoice was refunded; 'annulee': cancelled after a fraud (agreement, 12.3). */
  detail: { entreprise: string; le: string; montant: string; etat: 'a_verser' | 'versee' | 'retiree' | 'annulee' }[]
}

export interface PartnerProfile {
  code: string
  lien: string
  voie: 'clients' | 'publique'
  actif: boolean
  entente_acceptee_le: string | null
  /** The program is open and this partner has not accepted the current agreement. */
  entente_a_accepter: boolean
  compteurs: { clics: number; clics_ce_mois: number; inscriptions: number; essais: number; payants: number }
  primes: { a_recevoir: string; a_venir: string; recues: string }
  clients: PartnerClient[]
  /** Months with a statement, most recent first: "2026-11". */
  releves: string[]
  occasions: { id: number; entreprise: string; le: string; statut: 'en_attente' | 'rattachee' | 'refusee' }[]
  /** Public track only: 20 % of what the clients pay in their first 12 months. */
  commissions: PartnerCommissions | null
  /** Where the payments go. Changed only with a code sent by email. */
  versement: { mode: '' | 'interac' | 'paypal'; adresse: string }
}

export interface PartnerSpace {
  /** Show the settings section: a partner, or a client while the program is open. */
  visible: boolean
  programme: PartnerProgram
  partenaire: PartnerProfile | null
}

const KEY = ['partner-space']

/** The HTTP client of the door in use: the dashboard's (client session) by
 *  default, the partner address's own (partner session) inside its app. */
export const PartnerApiContext = createContext<AxiosInstance>(api)
export const usePartnerApi = () => useContext(PartnerApiContext)

export function usePartnerSpace() {
  const http = usePartnerApi()
  return useQuery({
    queryKey: KEY,
    queryFn: async () => (await http.get<PartnerSpace>('/api/partenaires/moi/')).data,
    staleTime: 60_000,
  })
}

/** Accepts the agreement in force, in the language the partner reads it in (French or English, both equally binding). */
export function useBecomePartner() {
  const http = usePartnerApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ version, langue }: { version: string; langue: 'fr' | 'en' }) =>
      (await http.post<PartnerSpace>('/api/partenaires/activer/', { entente_version: version, langue })).data,
    onSuccess: (data) => queryClient.setQueryData(KEY, data),
  })
}

export function useDeclareOpportunity() {
  const http = usePartnerApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { entreprise: string; contact: string; note: string }) =>
      (await http.post<PartnerSpace>('/api/partenaires/occasions/', body)).data,
    onSuccess: (data) => queryClient.setQueryData(KEY, data),
  })
}

/** Sends a code to the account's email: changing the payment method needs it. */
export function useRequestPayoutCode() {
  const http = usePartnerApi()
  return useMutation({ mutationFn: async () => (await http.post('/api/partenaires/versement/code/')).data })
}

export function useChangePayout() {
  const http = usePartnerApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { mode: string; adresse: string; code: string }) =>
      (await http.post<PartnerSpace>('/api/partenaires/versement/', body)).data,
    onSuccess: (data) => queryClient.setQueryData(KEY, data),
  })
}

/** The month's statement as a PDF file, saved by the browser. */
export async function downloadPartnerStatement(http: AxiosInstance, month: string, code: string) {
  const { data } = await http.get<Blob>('/api/partenaires/releve/', { params: { mois: month }, responseType: 'blob' })
  const url = URL.createObjectURL(data)
  const a = document.createElement('a')
  a.href = url
  a.download = `releve-${code}-${month}.pdf`
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
